import React, { memo, useContext, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useHistory, useParams } from 'react-router-dom';
import { context } from '../../../globalState/GlobalState';
import Spinner1 from '../../uiElements/spinners/spinner1/Spinner1';
import BreadCrumbs1 from '../../uiElements/breadCrumbs/breadCrumbs1/BreadCrumbs1';
import FormComponent1 from '../../uiElements/formComponents/formComponent1/FormComponent1';
import { isValidObject, isValidString } from '../../../catalog/Validity';
import { FORM_FIELD_MISSING } from '../../../catalog/NotificationsComments';
import { insertFormAPI } from '../../apiCalls/FormsSubmittedAPICalls';
import { FormItemType } from '../../../catalog/enums/FormItemType';
import { getEmailsForForm, getFormName } from '../../../catalog/Form';
import { getFormURL } from '../../../catalog/Social';
import { getSuccessURL } from '../../../catalog/Store';
import { showPriceFromCentsWithCurrency } from '../../../catalog/Price';

const initialState = {
    formItems: [],
    paymentOptionSelected: 0,
    savePressed: false,
    dataChanged: false,
    loadingSpinner: true,
};

function reducer(state, action) {
    switch (action.type) {
        case 'reset':
            return { ...initialState };
        case 'setFormItems':
            return { ...state, formItems: action.formItems };
        case 'setPaymentOption':
            return { ...state, paymentOptionSelected: action.option };
        case 'setFormItem':
            return { ...state,
                formItems: state.formItems.map((formItem) => {
                    if (formItem.id !== action.id) {
                        return formItem;
                    }
                    return { ...formItem, value: action.value, valueTitle: action.valueTitle };
                }) };
        case 'setSavePressed':
            return { ...state, savePressed: action.savePressed };
        case 'setDataChanged':
            return { ...state, dataChanged: action.dataChanged };
        case 'setLoadingSpinner':
            return { ...state, loadingSpinner: action.loadingSpinner };
        default:
            return initialState;
    }
}

const Form1 = (props) => {
    const { title, formItems, formPaymentItems, formType, published, loadingSpinner, secondaryLoadingSpinner } = props;
    const [state, setState] = useReducer(reducer, initialState);
    const { globalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const params = useParams();

    useEffect(() => {
        setState({ type: 'reset' });
        setState({ type: 'setLoadingSpinner', loadingSpinner: true });
        setState({ type: 'setFormItems',
            formItems: formItems.map((formItem) => {
                return { ...formItem, value: '', valueTitle: '' };
            }) });
        setState({ type: 'setLoadingSpinner', loadingSpinner: false });
    }, [history.location.pathname, formItems]);

    const getBreadCrumbsLinks = () => {
        return [
            {
                title: 'Home',
                link: '/',
            },
        ];
    };

    const checkFields = () => {
        let failed = false;
        state.formItems.forEach((formItem) => {
            if (formItem.required && !isValidString(formItem.value)) {
                enqueueSnackbar(FORM_FIELD_MISSING(formItem.title), { variant: 'error' });
                failed = true;
            }
        });

        if (failed) {
            return false;
        }

        return true;
    };

    const getEmails = () => {
        const emails = [];
        state.formItems.forEach((formItem) => {
            if (formItem.isEmail) {
                emails.push(formItem.value);
            }
        });
        return emails;
    };

    const getOtherOptions = () => {
        const formsOptions = state.formItems.map((formItem) => {
            if (formItem.type === FormItemType.TEXT) {
                return null;
            } if (formItem.type === FormItemType.TEXTFIELD_DATE) {
                if (isValidString(formItem.valueTitle)) {
                    const date = formItem.valueTitle.split('-');
                    const newDate = `${date[1]}/${date[2]}/${date[0]}`;
                    return { id: formItem.id, title: formItem.title, value: newDate };
                }
                return { id: formItem.id, title: formItem.title, value: '' };
            }
            return { id: formItem.id, title: formItem.title, value: formItem.valueTitle };
        }).filter((formItem) => {
            if (isValidObject(formItem)) {
                return true;
            }
            return false;
        });

        if (formPaymentItems.length > 0) {
            const formPaymentId = formPaymentItems[state.paymentOptionSelected].id;
            const formPaymentTitle = formPaymentItems[state.paymentOptionSelected].title;
            const formPaymentPrice = formPaymentItems[state.paymentOptionSelected].price;
            const formPaymentCurrency = formPaymentItems[state.paymentOptionSelected].currency;
            const formPaymentStripePriceId = formPaymentItems[state.paymentOptionSelected].stripePriceId;
            if (isValidString(formPaymentTitle)) {
                formsOptions.push({ id: 'payment-option', title: 'Payment Option', value: formPaymentTitle, paymentId: formPaymentId, paymentStripeId: formPaymentStripePriceId });
            }
            formsOptions.push({ id: 'payment-amount', title: 'Payment Amount', value: showPriceFromCentsWithCurrency(formPaymentPrice, formPaymentCurrency) });
        }

        return formsOptions;
    };

    const saveHandler = async () => {
        setState({ type: 'setSavePressed', savePressed: true });
        setState({ type: 'setLoadingSpinner', loadingSpinner: true });

        if (checkFields()) {
            await insertFormAPI(
                globalState.organizationId,
                globalState.teamId,
                globalState.sportsType,
                params.formid,
                formType,
                getFormName(formType, title, false),
                getEmails(),
                getEmailsForForm(globalState.form, globalState.teamEmail, params.formid),
                getOtherOptions(),
                setState,
                enqueueSnackbar,
                history,
                globalState.stripeAccountId,
                formPaymentItems.length > 0 ? [{ id: formPaymentItems[state.paymentOptionSelected].stripePriceId, quantity: 1 }] : [],
                // getFormURL(globalState, params.formid),
                getSuccessURL(globalState),
                getFormURL(globalState, params.formid),
            );
        }
        setState({ type: 'setLoadingSpinner', loadingSpinner: false });
    };

    return (
        <>
            {loadingSpinner ? <Spinner1 color={globalState.websiteStyles.colors.primaryColor} /> : (
                <>
                    <BreadCrumbs1 links={getBreadCrumbsLinks()} lastCrumb={title} marginLeft="100px" />
                    <FormComponent1
                        id={params.formid}
                        title={title}
                        formItems={state.formItems}
                        formPaymentItems={formPaymentItems}
                        paymentOptionSelected={state.paymentOptionSelected}
                        deleted={false}
                        published={published}
                        savePressed={state.savePressed}
                        loadingSpinner={secondaryLoadingSpinner}
                        submissionLoadingSpinner={state.loadingSpinner}
                        setState={setState}
                        saveHandler={saveHandler}
                    />
                </>
            )}
        </>
    );
};

Form1.propTypes = {
    title: PropTypes.string.isRequired,
    formItems: PropTypes.array.isRequired,
    formPaymentItems: PropTypes.array.isRequired,
    formType: PropTypes.number.isRequired,
    published: PropTypes.bool.isRequired,
    loadingSpinner: PropTypes.bool.isRequired,
    secondaryLoadingSpinner: PropTypes.bool.isRequired,
};

export default memo(Form1);
